import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import ProductCard from '../components/products/ProductCard';
import ProductFilter from '../components/products/ProductFilter';
import './ProductPage.css';

const ProductPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryFromUrl = queryParams.get('category') || '';
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [filters, setFilters] = useState({
    category_id: '',
    priceRange: '',
    brand: '',
    frameType: '',
    frameShape: '',
    frameMaterial: '',
    lensType: '',
    prescriptionType: '',
    gender: '',
    sortBy: 'newest'
  });
  const [eyeProblem, setEyeProblem] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Use ref to track initial load
  const initialLoadRef = useRef(true);
  const categoriesLoadedRef = useRef(false);

  // Fetch categories only once
  useEffect(() => {
    const fetchCategories = async () => {
      if (categoriesLoadedRef.current) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (response.ok) {
          const data = await response.json();
          const categoriesList = data.data || data.categories || data || [];
          setCategories(categoriesList);
          categoriesLoadedRef.current = true;
          
          // If we have a category from URL, find its ID
          if (categoryFromUrl && categoriesList.length > 0 && initialLoadRef.current) {
            const matchedCategory = categoriesList.find(
              cat => cat.name.toLowerCase() === categoryFromUrl.toLowerCase() || 
                     cat.slug?.toLowerCase() === categoryFromUrl.toLowerCase()
            );
            if (matchedCategory) {
              setFilters(prev => ({ ...prev, category_id: String(matchedCategory.id) }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, [categoryFromUrl]);

  // Optimized product fetch with debounce
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query string from filters
      const params = new URLSearchParams();
      
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.brand && filters.brand !== '') params.append('brand', filters.brand);
      if (filters.frameType && filters.frameType !== '') params.append('frame_type', filters.frameType);
      if (filters.frameShape && filters.frameShape !== '') params.append('frame_shape', filters.frameShape);
      if (filters.frameMaterial && filters.frameMaterial !== '') params.append('frame_material', filters.frameMaterial);
      if (filters.lensType && filters.lensType !== '') params.append('lens_type', filters.lensType);
      if (filters.prescriptionType && filters.prescriptionType !== '') params.append('prescription_type', filters.prescriptionType);
      if (filters.gender && filters.gender !== '') params.append('gender', filters.gender);
      
      // Price range filter
      if (filters.priceRange && filters.priceRange !== '') {
        const [min, max] = filters.priceRange.split('-');
        if (min) params.append('min_price', min);
        if (max && max !== '+') params.append('max_price', max);
      }
      
      // Sorting
      if (filters.sortBy) {
        let sortParam = '';
        switch(filters.sortBy) {
          case 'price-low': sortParam = 'price_asc'; break;
          case 'price-high': sortParam = 'price_desc'; break;
          case 'newest': sortParam = 'created_desc'; break;
          case 'popular': sortParam = 'popular'; break;
          default: sortParam = 'created_desc';
        }
        params.append('sort', sortParam);
      }

      const url = `${API_BASE_URL}/products?${params.toString()}`;
      console.log('Fetching:', url);
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.data || []);
      } else {
        setError('Failed to fetch products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Error loading products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch products when filters change (with debounce)
  useEffect(() => {
    // Debounce the fetch to avoid too many requests
    const timeoutId = setTimeout(() => {
      fetchProducts();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [fetchProducts]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    if (window.innerWidth <= 768) {
      setShowMobileFilter(false);
    }
  }, []);

  // Handle eye problem suggestion
  const handleEyeProblemSubmit = async (e) => {
    e.preventDefault();
    if (!eyeProblem) return;
    
    setShowSuggestions(true);
    
    let suggestedFilters = {};
    
    switch(eyeProblem.toLowerCase()) {
      case 'nearsighted':
      case 'myopia':
      case "can't see far":
        suggestedFilters = { prescriptionType: 'single-vision', lensType: 'standard' };
        break;
      case 'farsighted':
      case 'hyperopia':
      case "can't see close":
        suggestedFilters = { prescriptionType: 'single-vision', lensType: 'standard' };
        break;
      case 'astigmatism':
        suggestedFilters = { prescriptionType: 'toric', lensType: 'standard' };
        break;
      case 'presbyopia':
      case 'aging eyes':
      case 'need reading':
        suggestedFilters = { prescriptionType: 'progressive', lensType: 'standard' };
        break;
      case 'computer strain':
      case 'digital eye strain':
      case 'blue light':
        suggestedFilters = { lensType: 'blue-light-blocking' };
        break;
      case 'sensitive to light':
      case 'photophobia':
        suggestedFilters = { lensType: 'photochromic' };
        break;
      case 'dry eyes':
        suggestedFilters = { lensType: 'moisture-retention' };
        break;
      default:
        suggestedFilters = {};
    }
    
    handleFilterChange(suggestedFilters);
  };

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters({
      category_id: '',
      priceRange: '',
      brand: '',
      frameType: '',
      frameShape: '',
      frameMaterial: '',
      lensType: '',
      prescriptionType: '',
      gender: '',
      sortBy: 'newest'
    });
    setEyeProblem('');
    setShowSuggestions(false);
    window.history.pushState({}, '', window.location.pathname);
  }, []);

  // Toggle mobile filter
  const toggleMobileFilter = () => {
    setShowMobileFilter(!showMobileFilter);
    document.body.style.overflow = !showMobileFilter ? 'hidden' : 'auto';
  };

  const closeMobileFilter = () => {
    setShowMobileFilter(false);
    document.body.style.overflow = 'auto';
  };

  // Get selected category name
  const getSelectedCategoryName = useCallback(() => {
    if (filters.category_id) {
      const category = categories.find(c => String(c.id) === String(filters.category_id));
      return category ? category.name : '';
    }
    return categoryFromUrl || '';
  }, [filters.category_id, categories, categoryFromUrl]);

  return (
    <div className="product-page">
      <div className="container">
        {/* Header */}
        <div className="product-page-header">
          <h1 className="page-title">
            <span className="title-line">
              {getSelectedCategoryName() ? `${getSelectedCategoryName()} ` : 'Our '}
              Products
            </span>
            <span className="title-line highlight"></span>
          </h1>
          <p className="page-description">
            {getSelectedCategoryName() 
              ? `Browse our collection of ${getSelectedCategoryName()} eyewear` 
              : 'Discover the perfect eyewear for your needs'}
          </p>
        </div>

        {/* Active Filters Display */}
        {(filters.category_id || filters.brand || filters.frameType || filters.priceRange) && (
          <div className="active-filters-section">
            {filters.category_id && (
              <div className="active-filter-badge">
                <span>Category: {getSelectedCategoryName()}</span>
                <button onClick={() => handleFilterChange({ category_id: '' })} className="remove-filter">×</button>
              </div>
            )}
            {filters.brand && (
              <div className="active-filter-badge">
                <span>Brand: {filters.brand}</span>
                <button onClick={() => handleFilterChange({ brand: '' })} className="remove-filter">×</button>
              </div>
            )}
            {filters.frameType && (
              <div className="active-filter-badge">
                <span>Frame Type: {filters.frameType}</span>
                <button onClick={() => handleFilterChange({ frameType: '' })} className="remove-filter">×</button>
              </div>
            )}
            {filters.priceRange && (
              <div className="active-filter-badge">
                <span>Price: {
                  filters.priceRange === '0-1000' ? 'Under ₹1000' :
                  filters.priceRange === '1000-3000' ? '₹1000 - ₹3000' :
                  filters.priceRange === '3000-5000' ? '₹3000 - ₹5000' :
                  filters.priceRange === '5000-10000' ? '₹5000 - ₹10000' :
                  filters.priceRange === '10000-999999' ? 'Above ₹10000' : filters.priceRange
                }</span>
                <button onClick={() => handleFilterChange({ priceRange: '' })} className="remove-filter">×</button>
              </div>
            )}
            <button onClick={resetFilters} className="clear-all-filters">Clear All</button>
          </div>
        )}

        {/* Eye Problem Suggester - Simplified */}
        <div className="eye-problem-section">
          <div className="eye-problem-container">
            <h3 className="eye-problem-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="2">
                <circle cx="12" cy="12" r="2"/>
                <path d="M22 12c-2.667 4.667-6 7-10 7s-7.333-2.333-10-7c2.667-4.667 6-7 10-7s7.333 2.333 10 7z"/>
              </svg>
              Tell us about your eye concern
            </h3>
            <form className="eye-problem-form" onSubmit={handleEyeProblemSubmit}>
              <input
                type="text"
                placeholder="e.g., nearsighted, astigmatism, computer strain..."
                value={eyeProblem}
                onChange={(e) => setEyeProblem(e.target.value)}
                className="eye-problem-input"
              />
              <button type="submit" className="eye-problem-btn">
                Get Suggestions
              </button>
            </form>
            {showSuggestions && (
              <div className="suggestion-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <span>We've filtered products based on your concern</span>
                <button onClick={resetFilters} className="reset-suggestions">Reset</button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filter Button */}
        <div className="mobile-filter-header">
          <button className="mobile-filter-btn" onClick={toggleMobileFilter}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h16"/>
              <circle cx="18" cy="7" r="2" fill="white"/>
              <circle cx="6" cy="12" r="2" fill="white"/>
              <circle cx="14" cy="17" r="2" fill="white"/>
            </svg>
            Filters
            {Object.values(filters).filter(v => v !== '' && v !== 'newest').length > 0 && (
              <span className="filter-badge">
                {Object.values(filters).filter(v => v !== '' && v !== 'newest').length}
              </span>
            )}
          </button>
          <div className="mobile-sort">
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange({ sortBy: e.target.value })}
              className="mobile-sort-select"
            >
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>

        {/* Main Content */}
        <div className="product-layout">
          {/* Sidebar Filters - Desktop */}
          <aside className="product-sidebar desktop-sidebar">
            <ProductFilter 
              filters={filters}
              onFilterChange={handleFilterChange}
              categories={categories}
              onReset={resetFilters}
            />
          </aside>

          {/* Mobile Filter Overlay */}
          {showMobileFilter && (
            <div className="mobile-filter-overlay">
              <div className="mobile-filter-container">
                <div className="mobile-filter-header">
                  <h3>Filters</h3>
                  <button className="close-filter" onClick={closeMobileFilter}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div className="mobile-filter-content">
                  <ProductFilter 
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    categories={categories}
                    onReset={resetFilters}
                  />
                </div>
                <div className="mobile-filter-footer">
                  <button className="apply-filter-btn" onClick={closeMobileFilter}>
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Product Grid */}
          <main className="product-main">
            <div className="results-header desktop-only">
              <p className="results-count">
                {loading ? 'Loading...' : `${products.length} product${products.length !== 1 ? 's' : ''} found`}
              </p>
              <div className="sort-dropdown">
                <label htmlFor="sort">Sort by:</label>
                <select
                  id="sort"
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange({ sortBy: e.target.value })}
                  className="sort-select"
                >
                  <option value="newest">Newest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>
            </div>

            {/* Loading State with Skeleton */}
            {loading && (
              <div className="loading-grid">
                {[1,2,3,4,5,6,7,8].map(n => (
                  <div key={n} className="product-card-skeleton">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="error-message">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <h3>Failed to load products</h3>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-btn">
                  Try Again
                </button>
              </div>
            )}

            {/* No Products */}
            {!loading && !error && products.length === 0 && (
              <div className="no-products">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <h3>No products found</h3>
                <p>Try adjusting your filters or browse all products</p>
                <button onClick={resetFilters} className="reset-btn">
                  Clear All Filters
                </button>
              </div>
            )}

            {/* Products Grid */}
            {!loading && !error && products.length > 0 && (
              <div className="products-grid">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;